import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldCheck, Search, AlertTriangle, Info, AlertCircle, XCircle,
  CheckCircle, Wand2, Plus, Trash2, Edit3, X,
  FileText, Clock,
} from 'lucide-react'
import clsx from 'clsx'
import { Card, Badge, PageHeader, EmptyState } from '../components/Card'
import ErrorBoundary from '../components/ErrorBoundary'
import { useToast } from '../components/Toast'
import { layout } from '../utils/layout'
import {
  complianceScan,
  complianceHistory,
  type ComplianceIssueItem,
  type ComplianceScanResult,
} from '../api/client'

// ── Types ────────────────────────────────────────────────────────────────────

type IssueSeverity = 'info' | 'warning' | 'error' | 'critical'

interface ComplianceIssue {
  id: string
  severity: IssueSeverity
  rule: string
  description: string
  position: { start: number; end: number } | null
  suggestion: string | null
  auto_fixable: boolean
}

interface ScanResult {
  id: string
  content: string
  platform: string
  industry: string
  score: number
  issues: ComplianceIssue[]
  scanned_at: string
}

interface CustomRule {
  id: string
  name: string
  pattern: string
  severity: IssueSeverity
  message: string
  enabled: boolean
  created_at: string
}

const DEFAULT_CUSTOM_RULES: CustomRule[] = []

function toSeverity(value: string): IssueSeverity {
  switch (value) {
    case 'info':
    case 'warning':
    case 'error':
    case 'critical':
      return value
    default:
      return 'warning'
  }
}

function normalizeIssue(issue: ComplianceIssueItem): ComplianceIssue {
  return {
    ...issue,
    severity: toSeverity(issue.severity),
  }
}

function normalizeScan(scan: ComplianceScanResult): ScanResult {
  return {
    ...scan,
    issues: scan.issues.map(normalizeIssue),
  }
}

// ── Constants ────────────────────────────────────────────────────────────────

const PLATFORMS = ['Instagram', 'Twitter/X', 'Facebook', 'LinkedIn', 'TikTok', 'All Platforms']

const INDUSTRIES = [
  'General',
  'Healthcare / Pharma',
  'Financial Services',
  'Alcohol / Tobacco',
  'Real Estate',
  'Education',
  'Technology',
  'E-commerce',
  'Food & Beverage',
]

const SEVERITY_CONFIG: Record<IssueSeverity, {
  icon: typeof Info
  color: string
  bgColor: string
  borderColor: string
  label: string
  badgeColor: 'blue' | 'yellow' | 'red' | 'purple'
}> = {
  info:     { icon: Info,         color: 'text-blue-400',   bgColor: 'bg-blue-900/20',   borderColor: 'border-blue-800',   label: 'Info',     badgeColor: 'blue'   },
  warning:  { icon: AlertTriangle, color: 'text-yellow-400', bgColor: 'bg-yellow-900/20', borderColor: 'border-yellow-800', label: 'Warning',  badgeColor: 'yellow' },
  error:    { icon: AlertCircle,   color: 'text-red-400',    bgColor: 'bg-red-900/20',    borderColor: 'border-red-800',    label: 'Error',    badgeColor: 'red'    },
  critical: { icon: XCircle,       color: 'text-red-500',    bgColor: 'bg-red-950/30',    borderColor: 'border-red-700',    label: 'Critical', badgeColor: 'red'    },
}

// ── Loading Skeleton ─────────────────────────────────────────────────────────

function ComplianceSkeleton() {
  return (
    <div className="p-6 space-y-6 animate-pulse" role="status" aria-label="Loading compliance scanner">
      <div className="h-48 bg-slate-800/50 rounded-xl" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-64 bg-slate-800/50 rounded-xl" />
        <div className="h-64 bg-slate-800/50 rounded-xl" />
      </div>
      <span className="sr-only">Loading compliance scanner...</span>
    </div>
  )
}

// ── Score Gauge Component ────────────────────────────────────────────────────

function ScoreGauge({ score }: { score: number }) {
  const radius = 56
  const circumference = 2 * Math.PI * radius
  const fillPct = score / 100
  const dashOffset = circumference * (1 - fillPct)

  const scoreColor =
    score >= 80 ? '#22c55e' :
    score >= 60 ? '#f59e0b' :
    score >= 40 ? '#f97316' : '#ef4444'

  return (
    <div className="relative flex flex-col items-center" role="meter" aria-valuenow={score} aria-valuemin={0} aria-valuemax={100} aria-label={`Compliance score: ${score}`}>
      <svg width="140" height="140" viewBox="0 0 140 140" className="transform -rotate-90">
        {/* Background track */}
        <circle
          cx="70" cy="70" r={radius}
          fill="none" stroke="#1e293b" strokeWidth="10"
        />
        {/* Score arc */}
        <circle
          cx="70" cy="70" r={radius}
          fill="none" stroke={scoreColor} strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white">{score}</span>
        <span className="text-xs text-slate-500">/ 100</span>
      </div>
    </div>
  )
}

// ── Main View ────────────────────────────────────────────────────────────────

function ComplianceViewContent() {
  const toast = useToast()
  const qc = useQueryClient()

  // Scanner state
  const [content, setContent] = useState('')
  const [platform, setPlatform] = useState('All Platforms')
  const [industry, setIndustry] = useState('General')
  const [currentScan, setCurrentScan] = useState<ScanResult | null>(null)

  // Rule management state
  const [showRuleForm, setShowRuleForm] = useState(false)
  const [editingRule, setEditingRule] = useState<CustomRule | null>(null)
  const [ruleForm, setRuleForm] = useState({ name: '', pattern: '', severity: 'warning' as IssueSeverity, message: '' })

  // Queries
  const { data: historyData, isLoading } = useQuery({
    queryKey: ['compliance', 'history'],
    queryFn: async () => {
      const response = await complianceHistory(50)
      return { scans: response.scans.map(normalizeScan) }
    },
  })

  const { data: rulesData } = useQuery({
    queryKey: ['compliance', 'rules'],
    queryFn: async () => ({ rules: DEFAULT_CUSTOM_RULES }),
  })

  // Mutations
  const scanMut = useMutation({
    mutationFn: async () => normalizeScan(await complianceScan(content, platform, industry)),
    onSuccess: (result) => {
      setCurrentScan(result)
      if (result.score >= 80) {
        toast.success(`Scan complete -- score: ${result.score}/100`)
      } else if (result.score >= 50) {
        toast.warning(`Scan complete -- score: ${result.score}/100. Issues found.`)
      } else {
        toast.error(`Scan complete -- score: ${result.score}/100. Critical issues detected.`)
      }
      void qc.invalidateQueries({ queryKey: ['compliance', 'history'] })
    },
  })

  const applyFixMut = useMutation({
    mutationFn: async (issue: ComplianceIssue) => {
      await new Promise(r => setTimeout(r, 300))
      return { suggestion: issue.suggestion }
    },
    onSuccess: (data) => {
      if (data.suggestion) {
        setContent(data.suggestion)
        setCurrentScan(null)
        toast.success('Auto-fix applied. Re-scan to verify.')
      }
    },
  })

  const deleteRuleMut = useMutation({
    mutationFn: async (_id: string) => {
      await new Promise(r => setTimeout(r, 200))
      return { ok: true }
    },
    onSuccess: () => {
      toast.success('Rule deleted')
      void qc.invalidateQueries({ queryKey: ['compliance', 'rules'] })
    },
  })

  const saveRuleMut = useMutation({
    mutationFn: async () => {
      await new Promise(r => setTimeout(r, 300))
      return { ok: true, id: `rule-${Date.now()}` }
    },
    onSuccess: () => {
      toast.success(editingRule ? 'Rule updated' : 'Rule created')
      setShowRuleForm(false)
      setEditingRule(null)
      setRuleForm({ name: '', pattern: '', severity: 'warning', message: '' })
      void qc.invalidateQueries({ queryKey: ['compliance', 'rules'] })
    },
  })

  const handleScan = useCallback(() => {
    if (!content.trim()) {
      toast.warning('Please enter content to scan')
      return
    }
    scanMut.mutate()
  }, [content, scanMut, toast])

  const handleEditRule = useCallback((rule: CustomRule) => {
    setEditingRule(rule)
    setRuleForm({
      name: rule.name,
      pattern: rule.pattern,
      severity: rule.severity,
      message: rule.message,
    })
    setShowRuleForm(true)
  }, [])

  const rules = rulesData?.rules ?? []
  const history = historyData?.scans ?? []

  if (isLoading) return <ComplianceSkeleton />

  return (
    <div>
      <PageHeader
        title="Compliance Scanner"
        subtitle="AI-powered content compliance and regulatory checks"
        action={
          <Badge color="green">
            <ShieldCheck className="w-3 h-3 mr-1" />
            {rules.filter(r => r.enabled).length} rules active
          </Badge>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Content Scanner */}
        <Card title="Content Scanner">
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Content textarea */}
              <div className="lg:col-span-2">
                <label htmlFor="scan-content" className="text-xs text-slate-400 mb-1 block">
                  Content to scan
                </label>
                <textarea
                  id="scan-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste your social media content here for compliance scanning..."
                  className="w-full h-32 bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm resize-none"
                  aria-describedby="scan-help"
                />
                <p id="scan-help" className="text-[10px] text-slate-600 mt-1">
                  {content.length} characters
                </p>
              </div>

              {/* Controls */}
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-3">
                <div>
                  <label htmlFor="scan-platform" className="text-xs text-slate-400 mb-1 block">
                    Platform
                  </label>
                  <select
                    id="scan-platform"
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                  >
                    {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="scan-industry" className="text-xs text-slate-400 mb-1 block">
                    Industry
                  </label>
                  <select
                    id="scan-industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                  >
                    {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                  </select>
                </div>
              </div>

              {/* Scan button + score */}
              <div className="flex items-center gap-4 sm:flex-col sm:items-center sm:justify-center sm:gap-3">
                <div className="shrink-0">
                  {currentScan ? (
                    <div className="relative">
                      <ScoreGauge score={currentScan.score} />
                    </div>
                  ) : (
                    <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-full bg-slate-800/50 border-2 border-dashed border-slate-700 flex items-center justify-center">
                      <span className="text-[10px] sm:text-xs text-slate-600 text-center px-2">
                        Scan to see score
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleScan}
                  disabled={scanMut.isPending || !content.trim()}
                  className="flex-1 sm:flex-none flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm px-4 py-2.5 sm:py-2 rounded-lg transition-colors sm:w-full justify-center"
                  aria-label="Scan content for compliance issues"
                >
                  <Search className="w-4 h-4" />
                  {scanMut.isPending ? 'Scanning...' : 'Scan Content'}
                </button>
              </div>
            </div>
          </div>
        </Card>

        {/* Scan Results */}
        {currentScan && (
          <Card
            title={`Scan Results (${currentScan.issues.length} issue${currentScan.issues.length !== 1 ? 's' : ''})`}
            action={
              <button
                onClick={() => setCurrentScan(null)}
                className="p-1 rounded hover:bg-slate-700 text-slate-400"
                aria-label="Dismiss scan results"
              >
                <X className="w-4 h-4" />
              </button>
            }
          >
            {currentScan.issues.length === 0 ? (
              <div className="flex items-center gap-3 p-4 bg-green-900/20 border border-green-800 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" aria-hidden="true" />
                <div>
                  <div className="text-sm font-medium text-green-300">All Clear</div>
                  <div className="text-xs text-slate-400">
                    No compliance issues detected for {currentScan.platform} in {currentScan.industry}.
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {currentScan.issues.map(issue => {
                  const cfg = SEVERITY_CONFIG[issue.severity]
                  const Icon = cfg.icon
                  return (
                    <div
                      key={issue.id}
                      className={clsx(
                        'p-4 rounded-lg border',
                        cfg.bgColor,
                        cfg.borderColor,
                      )}
                      role="alert"
                    >
                      <div className="flex items-start gap-3">
                        <Icon className={clsx('w-4 h-4 shrink-0 mt-0.5', cfg.color)} aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <code className="text-xs text-slate-500 bg-slate-800/60 px-1.5 py-0.5 rounded">{issue.rule}</code>
                            <Badge color={cfg.badgeColor}>{cfg.label}</Badge>
                          </div>
                          <p className="text-sm text-slate-300 mb-1">{issue.description}</p>

                          {issue.position && (
                            <div className="text-xs text-slate-500 mb-2">
                              Position: characters {issue.position.start}-{issue.position.end}
                            </div>
                          )}

                          {issue.suggestion && (
                            <div className="mt-2 p-2 bg-slate-800/60 rounded-md">
                              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">
                                Suggested fix
                              </div>
                              <p className="text-xs text-slate-400 line-clamp-2">
                                {issue.suggestion.slice(0, 150)}
                                {issue.suggestion.length > 150 ? '...' : ''}
                              </p>
                            </div>
                          )}

                          {issue.auto_fixable && issue.suggestion && (
                            <button
                              onClick={() => applyFixMut.mutate(issue)}
                              disabled={applyFixMut.isPending}
                              className="mt-2 flex items-center gap-1 text-xs bg-brand-700 hover:bg-brand-600 text-brand-100 px-2.5 py-1.5 rounded-md transition-colors"
                              aria-label={`Apply auto-fix for ${issue.rule}`}
                            >
                              <Wand2 className="w-3 h-3" />
                              {applyFixMut.isPending ? 'Fixing...' : 'Auto-Fix'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        )}

        {/* Scan History + Custom Rules */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Scan History */}
          <Card title="Scan History">
            {!history.length ? (
              <EmptyState
                icon={<Clock className="w-10 h-10" />}
                title="No scans yet"
                description="Your compliance scan history will appear here."
              />
            ) : (
              <div className="space-y-2">
                {history.map(scan => {
                  const scoreColor =
                    scan.score >= 80 ? 'text-green-400' :
                    scan.score >= 60 ? 'text-yellow-400' :
                    scan.score >= 40 ? 'text-orange-400' : 'text-red-400'
                  return (
                    <button
                      key={scan.id}
                      onClick={() => {
                        setContent(scan.content)
                        setPlatform(scan.platform)
                        setIndustry(scan.industry)
                        setCurrentScan(scan)
                      }}
                      className="w-full text-left p-3 bg-slate-800/40 hover:bg-slate-800/60 rounded-lg transition-colors"
                      aria-label={`Load scan from ${new Date(scan.scanned_at).toLocaleString()}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-300 truncate">{scan.content}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-slate-500">{scan.platform}</span>
                            <span className="text-xs text-slate-700 hidden sm:inline">·</span>
                            <span className="text-xs text-slate-500 hidden sm:inline">{scan.industry}</span>
                            <span className="text-xs text-slate-700">·</span>
                            <span className="text-xs text-slate-500">
                              {scan.issues.length} issue{scan.issues.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end shrink-0">
                          <span className={clsx('text-sm font-bold', scoreColor)}>
                            {scan.score}
                          </span>
                          <time className="text-[10px] text-slate-600" dateTime={scan.scanned_at}>
                            {new Date(scan.scanned_at).toLocaleString('en', {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                            })}
                          </time>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </Card>

          {/* Custom Rules */}
          <Card
            title="Custom Rules"
            action={
              <button
                onClick={() => {
                  setEditingRule(null)
                  setRuleForm({ name: '', pattern: '', severity: 'warning', message: '' })
                  setShowRuleForm(true)
                }}
                className="flex items-center gap-1 text-xs bg-brand-600 hover:bg-brand-700 text-white px-2.5 py-1 rounded-md transition-colors"
                aria-label="Add custom rule"
              >
                <Plus className="w-3 h-3" />
                Add Rule
              </button>
            }
          >
            {!rules.length ? (
              <EmptyState
                icon={<FileText className="w-10 h-10" />}
                title="No custom rules"
                description="Create custom compliance rules for your specific needs."
              />
            ) : (
              <div className="space-y-2">
                {rules.map(rule => {
                  const cfg = SEVERITY_CONFIG[rule.severity]
                  return (
                    <div
                      key={rule.id}
                      className={clsx(
                        'p-3 rounded-lg border transition-opacity',
                        rule.enabled
                          ? 'bg-slate-800/40 border-surface-border'
                          : 'bg-slate-800/20 border-slate-800 opacity-60',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-slate-200">{rule.name}</span>
                            <Badge color={cfg.badgeColor}>{cfg.label}</Badge>
                            {!rule.enabled && (
                              <span className="text-[10px] text-slate-600">Disabled</span>
                            )}
                          </div>
                          <code className="text-[10px] text-slate-500 bg-slate-900 px-1.5 py-0.5 rounded block overflow-x-auto whitespace-nowrap">
                            {rule.pattern}
                          </code>
                          <p className="text-xs text-slate-400 mt-1">{rule.message}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => handleEditRule(rule)}
                            className="p-1.5 rounded hover:bg-slate-700 text-slate-500"
                            aria-label={`Edit rule ${rule.name}`}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteRuleMut.mutate(rule.id)}
                            disabled={deleteRuleMut.isPending}
                            className="p-1.5 rounded hover:bg-red-900/40 text-slate-500 hover:text-red-400"
                            aria-label={`Delete rule ${rule.name}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Create/Edit Rule Modal */}
      {showRuleForm && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setShowRuleForm(false)}
          role="dialog"
          aria-modal="true"
          aria-label={editingRule ? 'Edit compliance rule' : 'Create compliance rule'}
        >
          <div
            className="bg-surface-card border border-surface-border rounded-2xl p-4 sm:p-6 w-full max-w-md mx-4 sm:mx-0"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">
                {editingRule ? 'Edit Rule' : 'New Custom Rule'}
              </h2>
              <button
                onClick={() => setShowRuleForm(false)}
                className="p-1 rounded hover:bg-slate-700 text-slate-400"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="rule-name" className="text-xs text-slate-400 mb-1 block">Rule Name</label>
                <input
                  id="rule-name"
                  type="text"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="No competitor mentions"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label htmlFor="rule-pattern" className="text-xs text-slate-400 mb-1 block">
                  Regex Pattern
                </label>
                <input
                  id="rule-pattern"
                  type="text"
                  value={ruleForm.pattern}
                  onChange={(e) => setRuleForm(f => ({ ...f, pattern: e.target.value }))}
                  placeholder="(?i)(competitor|rival)"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm font-mono"
                />
                <p className="text-[10px] text-slate-600 mt-1">Uses regex syntax. (?i) for case-insensitive.</p>
              </div>

              <div>
                <label htmlFor="rule-severity" className="text-xs text-slate-400 mb-1 block">Severity</label>
                <select
                  id="rule-severity"
                  value={ruleForm.severity}
                  onChange={(e) => setRuleForm(f => ({ ...f, severity: e.target.value as IssueSeverity }))}
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label htmlFor="rule-message" className="text-xs text-slate-400 mb-1 block">Message</label>
                <input
                  id="rule-message"
                  type="text"
                  value={ruleForm.message}
                  onChange={(e) => setRuleForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Description shown when rule is triggered"
                  className="w-full bg-slate-800 border border-surface-border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowRuleForm(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-sm py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => saveRuleMut.mutate()}
                  disabled={saveRuleMut.isPending || !ruleForm.name || !ruleForm.pattern}
                  className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm py-2 rounded-lg"
                >
                  {saveRuleMut.isPending ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function ComplianceView() {
  return (
    <ErrorBoundary>
      <ComplianceViewContent />
    </ErrorBoundary>
  )
}
